import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

let pageScrollLockCount = 0;
let pageScrollLockY = 0;
let pageScrollLockRootHadAttribute = false;
let pageScrollLockRootOverflowY = '';
let pageScrollLockBodyWidth = '';
let pageScrollLockBodyPosition = '';
let pageScrollLockBodyTop = '';
let pageScrollLockBodyLeft = '';
let pageScrollLockBodyRight = '';

function lockPageScroll() {
  if (pageScrollLockCount === 0) {
    const root = document.documentElement;
    const body = document.body;
    const hasVerticalScrollbar = root.scrollHeight > window.innerHeight;

    pageScrollLockY = window.scrollY;
    pageScrollLockRootHadAttribute = root.hasAttribute('scroll-lock');
    pageScrollLockRootOverflowY = root.style.getPropertyValue('--scroll-lock-overflow-y');
    pageScrollLockBodyWidth = body.style.width;
    pageScrollLockBodyPosition = body.style.position;
    pageScrollLockBodyTop = body.style.top;
    pageScrollLockBodyLeft = body.style.left;
    pageScrollLockBodyRight = body.style.right;

    root.style.setProperty('--scroll-lock-overflow-y', hasVerticalScrollbar ? 'scroll' : 'visible');
    root.setAttribute('scroll-lock', '');

    body.style.width = '100%';
    body.style.position = 'fixed';
    body.style.top = `-${pageScrollLockY}px`;
    body.style.left = '0';
    body.style.right = '0';
  }

  pageScrollLockCount += 1;
}

function unlockPageScroll() {
  if (pageScrollLockCount === 0) return;

  pageScrollLockCount -= 1;
  if (pageScrollLockCount > 0) return;

  const root = document.documentElement;
  const body = document.body;

  if (pageScrollLockRootOverflowY) {
    root.style.setProperty('--scroll-lock-overflow-y', pageScrollLockRootOverflowY);
  } else {
    root.style.removeProperty('--scroll-lock-overflow-y');
  }

  if (pageScrollLockRootHadAttribute) {
    root.setAttribute('scroll-lock', '');
  } else {
    root.removeAttribute('scroll-lock');
  }

  body.style.width = pageScrollLockBodyWidth;
  body.style.position = pageScrollLockBodyPosition;
  body.style.top = pageScrollLockBodyTop;
  body.style.left = pageScrollLockBodyLeft;
  body.style.right = pageScrollLockBodyRight;

  window.scrollTo({ top: pageScrollLockY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockPageScroll();

      dialog.addEventListener('cancel', this.#handleCancel);
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleCancel);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockPageScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Routes native dialog cancel actions, such as mobile back gestures, through the theme close lifecycle.
   *
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockPageScroll();
        } else {
          unlockPageScroll();
        }
      }
    }
  },
  { capture: true }
);
